import { useAtom, useAtomValue } from "jotai";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { DarkTooltip } from "@components/DarkTooltip";
import report from "@report";
import { themeAtom, themePreferenceAtom, type ThemePreference } from "../../jotai/theme";
import IconSunLight from "./sun-light.svg?react";
import IconSunDark from "./sun-dark.svg?react";
import IconMoonLight from "./moon-light.svg?react";
import IconMoonDark from "./moon-dark.svg?react";
import IconSystemLight from "./system-light.svg?react";
import IconSystemDark from "./system-dark.svg?react";

type SegmentIconComponent = React.FC<React.SVGProps<SVGSVGElement>>;

interface SegmentDefinition {
  key: ThemePreference;
  label: string;
  // line: 非选中时使用的描边版本；solid: 选中时使用的实心版本
  line: SegmentIconComponent;
  solid: SegmentIconComponent;
}

const SEGMENTS: SegmentDefinition[] = [
  { key: "system", label: "System", line: IconSystemLight, solid: IconSystemDark },
  { key: "light", label: "Light", line: IconSunLight, solid: IconSunDark },
  { key: "dark", label: "Dark", line: IconMoonLight, solid: IconMoonDark },
];

export const ThemeToggleButton: React.FC = () => {
  const [preference, setPreference] = useAtom(themePreferenceAtom);
  // 订阅生效主题以便强制 selected segment 在主题切换时立即重渲染
  useAtomValue(themeAtom);
  const { t } = useTranslation();

  const handleChange = (next: ThemePreference) => {
    if (next === preference) return;
    setPreference(next);
    report.event("change_theme", {
      kalo_pos: "change_theme_switch",
      kalo_value: next,
    });
  };

  return (
    <div
      role="radiogroup"
      aria-label={t("Theme")}
      className="inline-flex h-[24px] items-center gap-[4px] rounded-full border border-kds-border-subtle bg-kds-bg p-[2px]"
    >
      {SEGMENTS.map(({ key, label, line: LineIcon, solid: SolidIcon }) => {
        const selected = preference === key;
        const translated = t(label);
        // 选中：实心 icon + 反色底（kds-fg 当 bg、kds-bg 当 fg），随主题自动反转。
        // 非选中：描边 icon + 弱化前景色，hover 提亮。
        const Icon = selected ? SolidIcon : LineIcon;
        return (
          <DarkTooltip
            key={key}
            title={translated}
            placement="top"
            mouseEnterDelay={0.3}
            styles={{
              container: {
                padding: "8px",
                borderRadius: 8,
                maxWidth: 240,
              },
            }}
          >
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={translated}
              onClick={() => handleChange(key)}
              className={classNames(
                "flex size-[20px] cursor-pointer items-center justify-center rounded-full outline-none transition-colors",
                selected ? "bg-kds-fg text-kds-bg" : "text-kds-fg-soft hover:text-kds-fg",
              )}
            >
              <Icon width={16} height={16} className="shrink-0" />
            </button>
          </DarkTooltip>
        );
      })}
    </div>
  );
};
